import {
  "profile-editors.birth_day" as dayLabel,
  "profile-editors.birth_month" as monthLabel,
  "profile-editors.birth_year" as yearLabel,
  "profile-editors.birth_years" as yearsText,
} from "@repo/i18n";
import { getLocale } from "@repo/i18n/runtime";
import { type ChangeEvent, useCallback, useId } from "react";
import { FieldError } from "./parts";
import { ageOn, type DayParts, daysInMonth } from "./payloads";

export interface BirthFormProps {
  error?: string;
  onChange: (value: DayParts) => void;
  /** Today, as a calendar day — passed in so the form reads no clock. */
  today: DayParts;
  value: DayParts;
}

/** The youngest age the year list offers, and how many years it spans (A2). */
const MIN_AGE = 14;
const YEAR_SPAN = 80;

const SELECT =
  "h-14 w-full rounded-lg bg-surface px-4 font-medium text-ink text-lg ring-1 ring-line focus:outline-none focus:ring-2 focus:ring-accent";

/**
 * Day, month, year and the age they make (A2 `BirthSheet`, editors.tsx:554-620).
 * Native selects: the platform's own wheel on Android is the fastest way to a
 * year forty rows down.
 */
export function BirthForm({ value, onChange, today, error }: BirthFormProps) {
  const id = useId();
  const days = daysInMonth(value.y, value.m);
  const locale = getLocale();
  const monthName = new Intl.DateTimeFormat(locale, {
    month: "long",
    timeZone: "UTC",
  });
  const years = Array.from(
    { length: YEAR_SPAN },
    (_, i) => today.y - MIN_AGE - i
  );

  const setDay = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) =>
      onChange({ ...value, d: Number(event.target.value) }),
    [onChange, value]
  );
  const setMonth = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) =>
      onChange({ ...value, m: Number(event.target.value) }),
    [onChange, value]
  );
  const setYear = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) =>
      onChange({ ...value, y: Number(event.target.value) }),
    [onChange, value]
  );

  return (
    <div>
      <div className="grid grid-cols-[1fr_1.6fr_1.2fr] gap-2">
        <label className="flex flex-col gap-1.5" htmlFor={`${id}-d`}>
          <span className="px-1 text-ink-3 text-sm">{dayLabel()}</span>
          <select
            className={SELECT}
            id={`${id}-d`}
            onChange={setDay}
            value={Math.min(value.d, days)}
          >
            {Array.from({ length: days }, (_, i) => i + 1).map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1.5" htmlFor={`${id}-m`}>
          <span className="px-1 text-ink-3 text-sm">{monthLabel()}</span>
          <select
            className={SELECT}
            id={`${id}-m`}
            onChange={setMonth}
            value={value.m}
          >
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>
                {monthName.format(new Date(Date.UTC(2000, m - 1, 1)))}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1.5" htmlFor={`${id}-y`}>
          <span className="px-1 text-ink-3 text-sm">{yearLabel()}</span>
          <select
            className={SELECT}
            id={`${id}-y`}
            onChange={setYear}
            value={value.y}
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="mt-5 text-center text-base text-ink-2">
        <span className="a-display text-3xl text-ink tabular-nums">
          {ageOn(value, today)}
        </span>{" "}
        {yearsText()}
      </p>
      {error ? <FieldError>{error}</FieldError> : null}
    </div>
  );
}
